import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Lock, Mail, KeyRound } from 'lucide-react';
import api from '../lib/api';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState('request'); // 'request' | 'verify' | 'done'
  const [forgotIdentifier, setForgotIdentifier] = useState(''); // phone or email
  const [forgotPhoneMasked, setForgotPhoneMasked] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPwd, setForgotNewPwd] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const roleRoutes = {
    manager: '/dashboard', director: '/dashboard',
    chef: '/kitchen', bartender: '/bar',
    till_supervisor: '/pos',
    shisha_expert: '/shisha', waiter: '/serving',
    dj: '/dj',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(roleRoutes[user.role] || '/dashboard');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const submitForgotRequest = async () => {
    if (!forgotIdentifier.trim()) { toast.error('Enter your email'); return; }
    setForgotSubmitting(true);
    try {
      const { data } = await api.post('/auth/forgot-password-otp', { email: forgotIdentifier.trim() });
      setForgotPhoneMasked(data?.email_masked || '');
      setForgotStep('verify');
      toast.success(data?.message || 'Code sent');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to send code');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const submitForgotVerify = async () => {
    if (!forgotCode.trim() || forgotCode.length < 6) { toast.error('Enter the 6-digit code'); return; }
    if (forgotNewPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setForgotSubmitting(true);
    try {
      await api.post('/auth/verify-otp-reset', {
        email: forgotIdentifier.trim(),
        code: forgotCode.trim(),
        new_password: forgotNewPwd,
      });
      setForgotStep('done');
      toast.success('Password reset! You can now sign in.');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Verification failed');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setTimeout(() => {
      setForgotStep('request');
      setForgotIdentifier('');
      setForgotPhoneMasked('');
      setForgotCode('');
      setForgotNewPwd('');
    }, 250);
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1680946496238-5272d3c407fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXN0YXVyYW50JTIwZGFya3xlbnwwfHx8fDE3NzYxOTIzMDd8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />

      <div className="relative z-10 w-full max-w-md px-6 animate-fadeIn">
        {/* Logo */}
        <div className="text-center mb-12" data-testid="app-title">
          <img src="/majnu-logo.png" alt="Majnu Lounge Bar Kitchen" className="h-28 w-auto mx-auto object-contain" />
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-transparent border-0 border-b border-white/20 rounded-none focus:border-gold focus:ring-0 text-white placeholder:text-zinc-600 h-12 font-sans"
                placeholder="staff@majnu.com.au"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-transparent border-0 border-b border-white/20 rounded-none focus:border-gold focus:ring-0 text-white placeholder:text-zinc-600 h-12 font-sans"
                placeholder="Enter password"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gold text-black font-bold tracking-wide rounded-sm hover:bg-gold-light transition-all duration-300 font-sans"
            data-testid="login-submit-button"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : 'Sign In'}
          </Button>

          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="w-full text-center text-gold/70 hover:text-gold text-xs font-sans tracking-wide transition-colors"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </form>

        <p className="text-center text-zinc-600 text-xs mt-8 font-sans tracking-wide">
          Staff access only. Contact your manager for credentials.
        </p>
      </div>

      {/* Forgot Password Dialog (Twilio SMS OTP) */}
      <Dialog open={forgotOpen} onOpenChange={(v) => { if (!v) closeForgot(); else setForgotOpen(true); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-gold" /> Reset Password
            </DialogTitle>
          </DialogHeader>
          {forgotStep === 'request' && (
            <div className="space-y-3 pt-2">
              <p className="text-xs font-sans text-zinc-400">Enter your email. We'll send a 6-digit code to your inbox.</p>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label>
                <Input
                  type="email"
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                  placeholder="staff@majnu.com.au"
                  className="bg-transparent border border-white/10 text-white font-sans"
                  data-testid="forgot-identifier-input"
                />
              </div>
              <Button onClick={submitForgotRequest} disabled={forgotSubmitting} className="w-full bg-gold text-black font-bold rounded-sm font-sans h-10" data-testid="forgot-send-code-btn">
                {forgotSubmitting ? 'Sending...' : 'Send Code'}
              </Button>
            </div>
          )}
          {forgotStep === 'verify' && (
            <div className="space-y-3 pt-2">
              <p className="text-xs font-sans text-zinc-400">A 6-digit code was sent to <span className="text-gold font-medium">{forgotPhoneMasked || 'your inbox'}</span>. Enter it below along with your new password.</p>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">6-Digit Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="bg-transparent border border-white/10 text-white font-sans tracking-[0.4em] text-center text-lg"
                  data-testid="forgot-code-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">New Password</Label>
                <Input
                  type="password"
                  value={forgotNewPwd}
                  onChange={(e) => setForgotNewPwd(e.target.value)}
                  placeholder="Min 6 characters"
                  className="bg-transparent border border-white/10 text-white font-sans"
                  data-testid="forgot-new-password-input"
                />
              </div>
              <Button onClick={submitForgotVerify} disabled={forgotSubmitting} className="w-full bg-gold text-black font-bold rounded-sm font-sans h-10" data-testid="forgot-verify-btn">
                {forgotSubmitting ? 'Verifying...' : 'Verify & Reset Password'}
              </Button>
              <button type="button" onClick={() => { setForgotStep('request'); setForgotCode(''); }} className="w-full text-zinc-500 hover:text-zinc-300 text-xs font-sans" data-testid="forgot-back-btn">
                Use a different email
              </button>
            </div>
          )}
          {forgotStep === 'done' && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-2 text-emerald-400 font-sans">
                <span className="text-2xl">✓</span>
                <p className="text-sm">Password reset successfully.</p>
              </div>
              <p className="text-xs font-sans text-zinc-500">You can now sign in with your new password.</p>
              <Button onClick={closeForgot} className="w-full bg-gold text-black font-bold rounded-sm font-sans h-10" data-testid="forgot-done-btn">
                Sign In
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
